import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopImage from "../../images/laptop_img.webp";
import useQRManager from "../../services/Dashboard/useQRManager";
import useDashboardAnalytics from "../../services/Dashboard/useDashboardAnalytics";
import ProductStatsModal from "./OrderStatsModal";
import useAuthApi from "../../services/LandingPage/useAuthApi";
import { CreditCard, Image, Info, X } from "lucide-react";
import useProfileDashboard from "../../services/Settings/useProfileDashboard";
import NoIndex from "../NoIndex";
import { Wallet, TrendingUp, ShoppingCart } from "lucide-react";
import usePayout from "../../services/Order/usePayout";
import usePayments from "../../services/Order/usePayments";
import { ToastContainer } from "react-toastify";

const campaigns = { used: 75, total: 100 };
const messages = { used: 25, total: 100 };

function ProgressBar({ used, total }) {
  const pct = Math.round((used / total) * 100);
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5">
      <div
        className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function Dashboard() {
  const [type, setType] = useState("volunteer");
  const [activeTab, setActiveTab] = useState("WhatsApp");
  const storedUser = JSON.parse(sessionStorage.getItem("user"));
  const userID = storedUser?.userid;
  const { qrCode, qrLinkVolunteer } = useQRManager(userID);
  const [analyticsDays, setAnalyticsDays] = useState(7);
  const [restaurantDays, setRestaurantDays] = useState(() => {
    return Number(localStorage.getItem("dashboard_restaurant_days")) || 7;
  });

  // Top Orders — POS

  const [shouldFetchRestaurantAnalytics, setShouldFetchRestaurantAnalytics] =
    useState(false);

  const [requestedRestaurantDays, setRequestedRestaurantDays] = useState(null);

  const selectedPlatform = activeTab === "POS" ? "pos" : "whatsapp";

  const {
    topOrders,
    topOrdersAnalytics,
    loading: bestSellingLoading,
  } = useDashboardAnalytics(userID, 30, selectedPlatform, {
    enableDashboard: false,
    enableOrderAnalytics: false,
    enableBestSelling: true,
    enableRestaurantAnalytics: false,
  });

  const orderAnalyticsCacheKey = `dashboard_order_analytics_${userID}_${restaurantDays}`;

  const {
    restaurantAnalytics,
    restaurantAnalyticsLoading,
    restaurantAnalyticsDuration,
  } = useDashboardAnalytics(
    userID,
    requestedRestaurantDays || restaurantDays,
    undefined,
    {
      enableDashboard: false,
      enableOrderAnalytics: false,
      enableBestSelling: false,
      enableRestaurantAnalytics: shouldFetchRestaurantAnalytics,
    },
  );

  const loading = bestSellingLoading;

  const finalOrders = topOrders || [];
  const finalAnalytics = topOrdersAnalytics || [];

  const [isProductStatsVisible, setIsProductStatsVisible] = useState(false);
  const tabs = ["WhatsApp", "POS"];
  const [subscription, setSubscription] = useState(null);
  const { fetchSubscriptionDetails } = useAuthApi();
  const { getWalletBalance, wallet } = usePayout();
  const { analytics, getAnalytics } = usePayments();
  const { getWhatsappCredentials } = useProfileDashboard();
  const [showPlanInfo, setShowPlanInfo] = useState(false);
  const [whatsappCred, setWhatsappCred] = useState(null);
  const [hasToken, setHasToken] = useState(false);
  const [haveCred, setHaveCred] = useState(false);
  const [credLoading, setCredLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [cachedRestaurantAnalytics, setCachedRestaurantAnalytics] = useState(
    [],
  );
  const [orderAnalyticsLoading, setOrderAnalyticsLoading] = useState(true);

  function ShimmerCard() {
    return (
      <div
        className="rounded-lg border border-gray-100 flex-shrink-0 overflow-hidden"
        style={{ minWidth: "100px", maxWidth: "120px", height: "90px" }}
      >
        <div className="animate-pulse flex flex-col items-center justify-center gap-2 p-2 h-full bg-gray-50">
          <div className="w-6 h-6 bg-gray-200 rounded-full" />
          <div className="w-14 h-3 bg-gray-200 rounded" />
          <div className="w-10 h-2 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  function ShimmerBar() {
    return (
      <div className="animate-pulse w-full">
        <div className="w-full h-7 bg-gray-200 rounded-lg" />
        <div className="flex gap-3 mt-2">
          <div className="w-20 h-3 bg-gray-200 rounded" />
          <div className="w-20 h-3 bg-gray-200 rounded" />
          <div className="w-20 h-3 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  const colorPool = [
    { color: "#6366F1", textColor: "#EEF2FF" },
    { color: "#F59E0B", textColor: "#FFFBEB" },
    { color: "#10B981", textColor: "#ECFDF5" },
    { color: "#F43F5E", textColor: "#FFF1F2" },
    { color: "#8B5CF6", textColor: "#F5F3FF" },
    { color: "#0EA5E9", textColor: "#F0F9FF" },
  ];

  const orderData = cachedRestaurantAnalytics?.[0] || {};

  const rowData = [
    {
      label: "Total Orders",
      value: orderData.total_orders || 0,
      color: "#0EA5E9",
      textColor: "#EEF2FF",
    },
    ...Object.keys(orderData)
      .filter((key) => key.endsWith("_order_count"))
      .map((key, index) => ({
        label: key
          .replace(/_order_count$/, "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase()),
        value: orderData[key] || 0,
        color: (colorPool[index] || colorPool[0]).color,
        textColor: (colorPool[index] || colorPool[0]).textColor,
      })),
  ];

  const total = rowData.reduce((sum, item) => sum + item.value, 0) || 1;

  const summaryFields = [
    {
      key: "revenue_settlement",
      isCombo: true,
      revenueIcon: <TrendingUp size={18} className="text-green-500" />,
      settlementIcon: <Wallet size={18} className="text-purple-500" />,
      bg: "bg-green-50",
      border: "border-green-100",
    },
    ...Object.keys(orderData)
      .filter(
        (key) =>
          key.endsWith("_amount") &&
          !["total_order_amount", "total_settlement_amount"].includes(key),
      )
      .map((key, index) => {
        const amountColorPool = [
          {
            icon: <CreditCard size={18} className="text-orange-500" />,
            bg: "bg-orange-50",
            border: "border-orange-100",
          },
          {
            icon: <CreditCard size={18} className="text-rose-500" />,
            bg: "bg-rose-50",
            border: "border-rose-100",
          },
          {
            icon: <CreditCard size={18} className="text-sky-500" />,
            bg: "bg-sky-50",
            border: "border-sky-100",
          },
          {
            icon: <CreditCard size={18} className="text-teal-500" />,
            bg: "bg-teal-50",
            border: "border-teal-100",
          },
        ];
        const style = amountColorPool[index] || amountColorPool[0];
        return {
          key,
          label: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          prefix: "₹",
          isAmount: true,
          ...style,
        };
      }),
  ];

  const totalDays = 365;
  const remaining = parseInt(subscription?.remaining_days || 14);
  const ratio = Math.min(remaining / totalDays, 1);

  const dotColor =
    ratio > 0.6 ? "#22C55E" : ratio > 0.3 ? "#F59E0B" : "#EF4444";

  const planDetails = [
    { label: "Plan", value: subscription?.fk_plan_id },
    {
      label: "Status",
      value: subscription?.is_active === "1" ? "Active" : "Inactive",
      className:
        subscription?.is_active === "1" ? "text-green-500" : "text-red-500",
    },
    { label: "Billing Cycle", value: subscription?.billing_cycle },
    {
      label: "Start Date",
      value: subscription?.start_date
        ? new Date(subscription.start_date).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "—",
    },
    {
      label: "Expiry Date",
      value: subscription?.expired_on
        ? new Date(subscription.expired_on).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "—",
    },
    {
      label: "Days Remaining",
      value: `${subscription?.remaining_days || "—"} days`,
      className: "text-blue-500",
    },
    {
      label: "Purchased On",
      value: subscription?.purchased_on
        ? new Date(subscription.purchased_on).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "—",
    },
  ];

  const walletBalance = parseFloat(wallet?.wallet_bal || 0);
  const unsettledAmount = parseFloat(wallet?.unsettled_Amount || 0);

  useEffect(() => {
    const getSubscription = async () => {
      const res = await fetchSubscriptionDetails(userID);

      if (res?.responseCode === "00") {
        setSubscription(res.data);
      }
    };

    if (userID) getSubscription();
  }, [userID]);

  useEffect(() => {
    const today = new Date();
    const fromDate = new Date();
    fromDate.setDate(today.getDate() - analyticsDays);

    const formatDate = (d) => d.toISOString().split("T")[0];

    getAnalytics(formatDate(fromDate), formatDate(today));
  }, [analyticsDays]);

  useEffect(() => {
    if (!userID) return;

    const cacheKey = `dashboard_order_analytics_${userID}_${restaurantDays}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      setCachedRestaurantAnalytics(JSON.parse(cachedData));
      setOrderAnalyticsLoading(false);
      setShouldFetchRestaurantAnalytics(false);
      setRequestedRestaurantDays(null);
      return;
    }

    setCachedRestaurantAnalytics([]);
    setOrderAnalyticsLoading(true);
    setRequestedRestaurantDays(restaurantDays);
    setShouldFetchRestaurantAnalytics(true);
  }, [userID, restaurantDays]);

  useEffect(() => {
    if (!userID || !shouldFetchRestaurantAnalytics) return;

    if (restaurantAnalyticsLoading) return;

    if (!requestedRestaurantDays) return;

    if (
      Number(restaurantAnalyticsDuration) !== Number(requestedRestaurantDays)
    ) {
      return;
    }

    const cacheKey = `dashboard_order_analytics_${userID}_${requestedRestaurantDays}`;

    sessionStorage.setItem(cacheKey, JSON.stringify(restaurantAnalytics || []));

    if (Number(requestedRestaurantDays) === Number(restaurantDays)) {
      setCachedRestaurantAnalytics(restaurantAnalytics || []);
    }

    setOrderAnalyticsLoading(false);
    setShouldFetchRestaurantAnalytics(false);
    setRequestedRestaurantDays(null);
  }, [
    restaurantAnalytics,
    restaurantAnalyticsLoading,
    restaurantAnalyticsDuration,
    requestedRestaurantDays,
    restaurantDays,
    userID,
    shouldFetchRestaurantAnalytics,
  ]);

  useEffect(() => {
    if (loading || credLoading) return;

    const tempAlerts = [];
    const remainingDays = Number(subscription?.remaining_days || 0);

    // 1. SETUP
    if (!haveCred) {
      tempAlerts.push({
        type: "setup",
        message:
          "Please complete your one-time setup to start Anlook Services.",
        button: "Complete Setup",
        route: "/Profile/Setup",
        color: "bg-red-100 text-red-700",
        btnColor: "bg-red-500",
      });
    }

    //  2. WHATSAPP ONBOARDING
    if (hasToken) {
      tempAlerts.push({
        type: "whatsapp",
        message:
          "Kindly complete the WhatsApp onboarding process to start using our WhatsApp service.",
        button: "Complete Onboarding",
        route: "/WhatsApp/Onboarding",
        color: "bg-blue-100 text-blue-700",
        btnColor: "bg-blue-500",
      });
    }

    //  3. UPGRADE PLAN
    if (remainingDays <= 0) {
      tempAlerts.push({
        type: "upgrade",
        message: "Your plan has expired. Please upgrade to continue services.",
        button: "Upgrade Plan",
        route: "/Profile/Plans",
        color: "bg-green-100 text-green-700",
        btnColor: "bg-green-600",
      });
    } else if (remainingDays <= 5) {
      tempAlerts.push({
        type: "upgrade",
        message: `Your subscription will expire in ${remainingDays} days. Please renew soon!`,
        button: "Upgrade Plan",
        route: "/Profile/Plans",
        color: "bg-green-100 text-green-700",
        btnColor: "bg-green-600",
      });
    }

    setAlerts(tempAlerts);
  }, [loading, credLoading, subscription, hasToken, haveCred]);

  useEffect(() => {
    const fetchWhatsappCred = async () => {
      setCredLoading(true);

      const res = await getWhatsappCredentials(userID);

      if (res?.responseCode === "00") {
        setWhatsappCred(res.response);

        const token = res?.response?.token || res?.response?.access_token;

        if (!token) {
          setHasToken(true);
        } else {
          setHasToken(false);
        }

        setHaveCred(true);
      } else if (res?.responseCode === "01") {
        setHaveCred(false);
        setHasToken(true);
      }

      setCredLoading(false);
    };

    if (userID) fetchWhatsappCred();
  }, [userID]);

  useEffect(() => {
    getWalletBalance();
  }, []);

  return (
    <>
      <NoIndex />
<ToastContainer/>
      {/* ================= TOP BANNER ================= */}
      {alerts.map((alert, index) => (
        <div
          key={index}
          className={`w-full px-4 py-2 flex justify-between items-center text-sm  montserrat-font-1 ${alert.color}`}
        >
          <span className="flex-1 text-center">{alert.message}</span>

          <div className="flex items-center gap-3">
            <Link to={alert.route}>
              <button
                className={`text-white px-3 py-1 rounded ${alert.btnColor}`}
              >
                {alert.button}
              </button>
            </Link>

            {/*  Close button */}
            <button
              onClick={() =>
                setAlerts((prev) => prev.filter((_, i) => i !== index))
              }
              className="text-black font-bold"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      ))}

      <div className="p-4 h-full flex flex-col overflow-hidden">
        {/* Top Banner */}
        <div className="mb-6 flex-shrink-0">
          <div className="flex flex-wrap justify-between rounded-2xl bg-gradient-to-r from-blue-500 to-teal-400 relative overflow-visible min-h-[160px]">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute w-40 h-40 bg-white/10 rounded-full -top-10 -left-10 blur-2xl" />
              <div className="absolute w-56 h-56 bg-white/10 rounded-full -bottom-16 right-10 blur-2xl" />
            </div>

            {/* LEFT TEXT */}
            <div className="w-full md:w-7/12 p-5 relative z-10 mt-3">
              <div className="text-white text-lg md:text-3xl font-bold leading-snug montserrat-font-1">
                Integrated Your System with Anlook and Maximize All Benefits
              </div>

              <div className="text-white text-lg md:text-lg font-semibold mt-3 montserrat-font-2">
                Connect your tools, launch campaign, and drive revenue
                <br /> all from one intelligent workspace.
              </div>

              <div className="pt-4">
                <Link to="/Profile/Plans">
                  <button className="bg-green-600 text-white font-semibold px-5 py-2 rounded-lg transition shadow montserrat-font-1">
                    Upgrade Plan
                  </button>
                </Link>
              </div>
            </div>

            {/* IMAGE */}
            <div className="hidden md:flex w-4/12 justify-end items-end overflow-hidden relative z-10 mt-5 mr-5 mb-3">
              <img src={TopImage} alt="" className="h-[200px] object-contain" />
            </div>
          </div>
        </div>
<div className="flex-1 overflow-y-auto min-h-0 pr-2">

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 montserrat-font-1">
          {[
            {
              title: "Wallet Balance",
              value: `₹ ${walletBalance.toFixed(2)}`,
              icon: <Wallet className="text-blue-500" />,
            },
            {
              title: "Unsettled Amount",
              value: `₹ ${unsettledAmount.toFixed(2)}`,
              icon: <CreditCard className="text-orange-500" />,
            },
            {
              title: "Conversion Rate",
              value: "6.84%",
              icon: <TrendingUp className="text-orange-500" />,
            },
            {
              title: "Avg Order Value",
              value: "₹550",
              icon: <ShoppingCart className="text-purple-500" />,
            },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white p-4 rounded-xl shadow-sm flex items-center gap-4 montserrat-font-1"
            >
              {/* ICON */}
              <div className="p-3 bg-gray-100 rounded-lg">{item.icon}</div>

              {/* TEXT */}
              <div>
                <p className="text-gray-500 text-sm montserrat-font-2">
                  {item.title}
                </p>
                <h3 className="text-xl font-semibold mt-1 montserrat-font-2">
                  {item.value}
                </h3>
              </div>
            </div>
          ))}
        </div>

        {/* Middle Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-5">
          {/* Campaign Overview */}
          <div className="bg-white rounded-xl p-4 shadow-sm col-span-2">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold montserrat-font-1">
                Campaign Overview
              </h3>
              <Link
                to="/WhatsApp/CreateCampaign"
                className="inline-block text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md transition-colors montserrat-font-1"
              >
                + Create
              </Link>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mt-3 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-blue-600">51</h2>
                <p className="text-sm text-gray-600 montserrat-font-1">
                  Total Campaigns
                </p>
              </div>
              <Link
                to="/WhatsApp/Manage"
                className="text-xs border px-2 py-1 rounded-md montserrat-font-1"
              >
                View All Campaign
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-green-600 font-semibold montserrat-font-1">
                  14
                </p>
                <p className="montserrat-font-1">Active Campaign</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <p className="text-orange-600 font-semibold montserrat-font-1">
                  1,248
                </p>
                <p className="montserrat-font-1">Paused Campaign</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-blue-600 font-semibold montserrat-font-1">
                  1,248
                </p>
                <p className="montserrat-font-1">Completed Campaign</p>
              </div>
            </div>
          </div>

          {/* Orders Analytics */}

          {/* Orders Analytics */}
          <div className="bg-white rounded-xl p-4 shadow-sm montserrat-font-1">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold montserrat-font-1">
                Orders Analytics
              </h3>
              <select
                value={restaurantDays}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setRestaurantDays(value);
                  localStorage.setItem("dashboard_restaurant_days", value);
                }}
                className="border px-2 py-1 rounded text-sm"
              >
                <option value={7}>Last 7 Days</option>
                <option value={15}>Last 15 Days</option>
                <option value={30}>Last 30 Days</option>
              </select>
            </div>

            {/* Summary Cards */}
            <div
              className="hide-scroll flex gap-2 mb-3 pb-1"
              style={{
                overflowX: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <style>{`.hide-scroll::-webkit-scrollbar { display: none; }`}</style>

              {orderAnalyticsLoading ? (
                <>
                  <ShimmerCard />
                  <ShimmerCard />
                  <ShimmerCard />
                </>
              ) : (
                summaryFields.map(
                  ({
                    key,
                    label,
                    prefix,
                    isAmount,
                    icon,
                    revenueIcon,
                    settlementIcon,
                    bg,
                    border,
                    isCombo,
                  }) => {
                    if (isCombo) {
                      return (
                        <div
                          key="combo"
                          className={`${bg} rounded-lg p-2 text-center border gap-2 ${border} flex-shrink-0`}
                          style={{ minWidth: "300px", maxWidth: "350px" }}
                        >
                          <div className="flex items-center justify-center gap-2 mb-1">
                            {revenueIcon}
                          </div>
                          <p
                            className="font-semibold text-gray-800 text-sm"
                            style={{
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            ₹
                            {Number(
                              orderData.total_order_amount || 0,
                            ).toLocaleString("en-IN")}
                          </p>
                          <p
                            className="text-gray-400"
                            style={{ fontSize: "10px" }}
                          >
                            Revenue
                          </p>
                          <div className="border-t border-green-200 my-1" />
                          <div className="flex items-center justify-center gap-1 mb-1">
                            {settlementIcon}
                          </div>
                          <p
                            className="font-semibold text-gray-800 text-sm"
                            style={{
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            ₹
                            {Number(
                              orderData.total_settlement_amount || 0,
                            ).toLocaleString("en-IN")}
                          </p>
                          <p
                            className="text-gray-400"
                            style={{ fontSize: "10px" }}
                          >
                            Settlement
                          </p>
                        </div>
                      );
                    }

                    const value = orderData[key] ?? 0;
                    return (
                      <div
                        key={key}
                        className={`${bg} rounded-lg p-2 text-center border ${border} flex-shrink-0`}
                        style={{ minWidth: "100px", maxWidth: "120px" }}
                      >
                        <div className="flex items-center justify-center mb-1">
                          {icon}
                        </div>
                        <p
                          className="font-semibold text-gray-800 text-sm"
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {prefix}
                          {isAmount
                            ? Number(value).toLocaleString("en-IN")
                            : value}
                        </p>
                        <p
                          className="text-gray-400"
                          style={{
                            fontSize: "10px",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {label}
                        </p>
                      </div>
                    );
                  },
                )
              )}
            </div>

            {/* Stacked Bar */}
            <div className="w-full montserrat-font-1">
              {!orderData || Object.keys(orderData).length === 0 ? (
                <ShimmerBar />
              ) : (
                <>
                  <div
                    className="flex w-full overflow-hidden border rounded-lg"
                    style={{ height: "28px" }}
                  >
                    {rowData.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-center font-medium"
                        style={{
                          width: `${(item.value / total) * 100}%`,
                          backgroundColor: item.color,
                          color: item.textColor,
                          fontSize: "11px",
                        }}
                      >
                        {(item.value / total) * 100 > 10 &&
                          `${item.value} ${item.label}`}
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-3 mt-2 montserrat-font-1">
                    {rowData.map((item, i) => (
                      <span
                        key={i}
                        className="flex items-center gap-1"
                        style={{ fontSize: "11px", color: "gray" }}
                      >
                        <span
                          className="inline-block rounded-sm flex-shrink-0"
                          style={{
                            width: "8px",
                            height: "8px",
                            backgroundColor: item.color,
                          }}
                        />
                        {item.label} — {item.value}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-5">
          {/* QR Code */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            {/* Header with Dropdown */}
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg text-gray-800 font-bold montserrat-font-1">
                QR CODE
              </h3>

              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="text-sm border rounded-md px-2 py-1 outline-none"
              >
                <option value="volunteer">Volunteer</option>
                <option value="chat">Chat</option>
              </select>
            </div>

            {/* QR Image — bigger + more top space */}
            <div className="flex justify-center mt-10">
              {type === "volunteer" ? (
                qrLinkVolunteer ? (
                  <img
                    src={qrLinkVolunteer}
                    className="w-72 h-72 object-contain"
                  />
                ) : (
                  <div className="w-72 h-72 flex items-center justify-center text-gray-400 text-sm">
                    Loading QR...
                  </div>
                )
              ) : qrCode ? (
                <img src={qrCode} className="w-72 h-72 object-contain" />
              ) : (
                <div className="w-72 h-72 flex items-center justify-center text-gray-400 text-sm">
                  Loading QR...
                </div>
              )}
            </div>

            {/* Button */}
            <button className="mt-8 w-full bg-blue-500 text-white py-2 rounded-md text-sm montserrat-font-1">
              {type === "volunteer"
                ? "Scan to Start Volunteer Shift"
                : "Scan to Start Chat"}
            </button>
          </div>

          {/* Active Plan */}
          <div className="bg-white rounded-3xl border border-gray-100 p-4 w-full relative montserrat-font-1">
            {/* Header - Reduced margin */}
            <div className="flex items-start justify-between mb-1">
              <div>
                <p className="text-lg font-bold text-gray-800 leading-tight montserrat-font-2">
                  Active Plan
                </p>
                <p className="text-sm text-gray-500">Current Subscription</p>
              </div>
              <button
                onClick={() => setShowPlanInfo(!showPlanInfo)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-blue-500 transition-colors"
              >
                <Info size={20} />
              </button>
            </div>

            {/* Plan Info Tooltip */}
            {showPlanInfo && (
              <div className="absolute top-12 right-4 z-20 bg-white border montserrat-font-1  border-gray-100 rounded-xl shadow-lg p-4 w-64 text-sm">
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowPlanInfo(false)}
                />
                <div className="relative z-20">
                  <p className="font-semibold text-gray-800 mb-2 border-b pb-1 montserrat-font-2">
                    Plan Details
                  </p>
                  <div className="space-y-1.5 text-xs">
                    {planDetails.map(({ label, value, className }) => (
                      <div key={label} className="flex justify-between">
                        <span className="text-gray-400">{label}</span>
                        <span
                          className={`font-medium ${className || "text-gray-700"}`}
                        >
                          {value || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Gauge SVG - Height reduced to h-36 and math centered at X=110 */}
            <div className="flex justify-center items-center montserrat-font-1">
              <svg viewBox="0 0 220 160" className="w-full h-36">
                <defs>
                  <linearGradient
                    id="gaugeGrad"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#EF4444" />
                    <stop offset="40%" stopColor="#EF4444" />
                    <stop offset="62%" stopColor="#F59E0B" />
                    <stop offset="80%" stopColor="#FCD34D" />
                    <stop offset="100%" stopColor="#22C55E" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Dotted background track - Centered at 110 */}
                {Array.from({ length: 36 }).map((_, i) => {
                  const a = -180 + i * 5;
                  const r = (a * Math.PI) / 180;
                  const cx = 110 + 90 * Math.cos(r); // Radius reduced to 90 to fit better
                  const cy = 130 + 90 * Math.sin(r);
                  return (
                    <circle key={i} cx={cx} cy={cy} r="3" fill="#E5E7EB" />
                  );
                })}

                {/* Colored arc - Centered at 110 */}
                <path
                  d="M 20 130 A 90 90 0 0 1 200 130"
                  fill="none"
                  stroke="url(#gaugeGrad)"
                  strokeWidth="12"
                  strokeLinecap="round"
                />

                {/* Dynamic Indicator Logic */}
                {(() => {
                  const percentage = Math.min(
                    parseInt(subscription?.remaining_days || 14) / 365,
                    1,
                  );
                  const angle = -180 + percentage * 180;
                  const rad = (angle * Math.PI) / 180;
                  const cx = 110 + 90 * Math.cos(rad);
                  const cy = 130 + 90 * Math.sin(rad);

                  return (
                    <g>
                      <circle
                        cx={cx}
                        cy={cy}
                        r="18"
                        fill={dotColor}
                        opacity="0.2"
                        filter="url(#glow)"
                      />
                      <circle
                        cx={cx}
                        cy={cy}
                        r="10"
                        fill="white"
                        stroke={dotColor}
                        strokeWidth="3"
                      />
                      <circle cx={cx} cy={cy} r="5" fill={dotColor} />
                    </g>
                  );
                })()}

                <text
                  x="110"
                  y="130"
                  textAnchor="middle"
                  fontSize="22"
                  fontWeight="700"
                  fill="#111827"
                  fontFamily="sans-serif"
                >
                  {subscription?.fk_plan_id || "Basic"}
                </text>
              </svg>
            </div>

            {/* Days remaining - Tightened spacing */}
            <div className="text-center mb-3">
              <p className="text-lg font-bold text-gray-800 montserrat-font-2">
                {subscription?.remaining_days || "14"} Days Remaining
              </p>
              <p className="text-xs text-gray-500 montserrat-font-2">
                Renew on{" "}
                {subscription?.expired_on
                  ? new Date(subscription.expired_on).toLocaleDateString(
                      "en-IN",
                      { day: "numeric", month: "short", year: "numeric" },
                    )
                  : "Apr 14, 2026"}
              </p>

              <div className="flex items-center justify-center gap-2 mt-2">
                <span
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${subscription?.is_active === "1" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${subscription?.is_active === "1" ? "bg-green-500" : "bg-red-500"}`}
                  />
                  {subscription?.is_active === "1" ? "Active" : "Inactive"}
                </span>
                <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-500">
                  {subscription?.billing_cycle || "Monthly"}
                </span>
              </div>
            </div>

            {/* Upgrade button - Smaller padding */}
            <div className="flex justify-center mb-4 montserrat-font-1">
              <Link to="/Profile/Plans" className="w-full max-w-[200px]">
                <button className="w-full py-2 rounded-full text-sm font-semibold text-white bg-blue-500 hover:bg-blue-600 hover:scale-105 transition-all shadow-md montserrat-font-1 shadow-blue-100">
                  Upgrade Plan
                </button>
              </Link>
            </div>

            {/* Progress bars - Reduced gap */}
            <div className="border-t border-gray-100 space-y-2 montserrat-font-1">
              {/* Campaigns */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-2 rounded-xl shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-blue-700 montserrat-font-1">
                    Campaigns Usage
                  </span>
                  <span className="text-xs font-medium text-blue-600 bg-white px-2 py-0.5 rounded-md">
                    {campaigns.used}/{campaigns.total}
                  </span>
                </div>

                <ProgressBar used={campaigns.used} total={campaigns.total} />

                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>Used</span>
                  <span>
                    {Math.round((campaigns.used / campaigns.total) * 100)}%
                  </span>
                </div>
              </div>

              {/* Messages */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-2 rounded-xl shadow-sm montserrat-font-1">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-purple-700 montserrat-font-1">
                    Messages Usage
                  </span>
                  <span className="text-xs font-medium text-purple-600 bg-white px-2 py-0.5 rounded-md">
                    {messages.used}/{messages.total}
                  </span>
                </div>

                <ProgressBar used={messages.used} total={messages.total} />

                <div className="flex justify-between text-xs text-gray-500 mt-2 montserrat-font-2">
                  <span>Used</span>
                  <span>
                    {Math.round((messages.used / messages.total) * 100)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Selling Orders */}

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm w-full max-w-4xl p-5 montserrat-font-1">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl text-gray-800 font-bold montserrat-font-1">
                  Top Selling Orders
                </h2>
                <p className="text-lg text-gray-600 mt-0.5 montserrat-font-2">
                  Revenue & volume by channel
                </p>
              </div>
              <span
                onClick={() => setIsProductStatsVisible(true)}
                className="text-white text-sm cursor-pointer font-medium border p-2 montserrat-font-1 bg-blue-500 rounded-lg montserrat-font-2"
              >
                View Analytics
              </span>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 border-b border-gray-200 mb-3 montserrat-font-1">
              {tabs.map((tab) => {
                const isActive = activeTab === tab;

                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`relative pb-2 text-sm font-semibold transition-all montserrat-font-1 ${
                      isActive
                        ? "text-blue-600"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    {tab}

                    {isActive && (
                      <span className="absolute left-0 -bottom-[1px] h-[3px] w-full rounded-full bg-blue-600" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* List */}
            <div className="divide-y divide-gray-50 max-h-[350px] overflow-y-auto montserrat-font-1">
              {loading ? (
                <div className="text-center py-6 text-sm text-gray-400">
                  Loading...
                </div>
              ) : finalOrders?.length > 0 ? (
                finalOrders.slice(0, 5).map((item) => (
                  <div
                    key={item.id || item.productId}
                    className="flex items-center gap-3 py-3.5 hover:bg-gray-50 rounded-xl px-2 transition-colors"
                  >
                    {/* Image */}
                    <div className="w-10 h-10 rounded-full border flex items-center justify-center overflow-hidden bg-gray-100">
                      {item?.productImage && item.productImage !== "" ? (
                        <img
                          src={item.productImage}
                          alt={item?.productName || "product"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Image size={24} className="text-gray-400" />
                      )}
                    </div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate montserrat-font-2">
                        {item.productName}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5 montserrat-font-2">
                        #{item.productId || item.id}
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs bg-blue-50 text-blue-500 px-2 py-1 rounded-full montserrat-font-2">
                        {item.orderCount} Sold
                      </span>

                      <span className="text-xs bg-purple-50 text-purple-400 px-2 py-1 rounded-full montserrat-font-2">
                        {(
                          item.orderPlaced || item.orderCount
                        )?.toLocaleString()}{" "}
                        Orders
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-sm text-gray-400 montserrat-font-1 montserrat-font-1">
                  No Data Found ({activeTab})
                </div>
              )}
            </div>
          </div>
        </div>

        {isProductStatsVisible && finalAnalytics?.length > 0 && (
          <ProductStatsModal
            data={finalAnalytics}
            onClose={() => setIsProductStatsVisible(false)}
          />
        )}
      </div>
      </div>
    </>
  );
}
